/**
 * {@link DataTable} for assessments.
 */
import * as React from 'react';
import { ClipboardList } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { AssessmentRow } from './AssessmentRow';
import { AssessmentCard } from './AssessmentCard';
import { AssessmentFiltersContent } from './AssessmentFilters';
import { AssessmentDetailsPanel } from '@/components/assessments/renderAssessmentDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getAllAssessments,
  deleteAssessment,
  validateAssessment,
  exportAssessment,
} from '@/services/assessments.service';
import { buildListParams, formatAssessmentUserName } from './assessment-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { usePageActions } from '@/hooks/usePageActions';
import { cn } from '@/lib/utils';
import {
  ASSESSMENT_TYPE_DEFINITIONS,
  DEFAULT_ASSESSMENT_FILTERS,
  ALL_ASSESSMENT_FILTER_GROUP_IDS,
  ASSESSMENT_FILTER_GROUP_LABELS,
  type AssessmentFilterGroupId,
} from './assessment-configs';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { Assessment, AssessmentFilters } from '@/types/assessment.types';

/** Props for {@link AssessmentsDataTable}. */
interface AssessmentsDataTableProps {
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: 'full' | '1/2' | '2/3' | '1/3' | 'auto';
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditAssessment?: (assessmentId: string) => void;
  enableFilters?: boolean;
  defaultFilters?: AssessmentFilters;
  /** Dashboard section: the create action lives in the toolbar instead of the page header. */
  showCreateButton?: boolean;
  onCreateAssessment?: () => void;
  isSection?: boolean;
}

/** {@link DataTable} for assessments. */
export const AssessmentsDataTable: React.FC<AssessmentsDataTableProps> = ({
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth = 'full',
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditAssessment,
  enableFilters = true,
  defaultFilters = DEFAULT_ASSESSMENT_FILTERS,
  showCreateButton = false,
  onCreateAssessment,
  isSection = false,
}) => {
  const queryClient = useQueryClient();
  const { canEditRow, canDeleteRow, canValidate, canExportCsv } = usePageActions('assessments');

  const dt = useDataTable<AssessmentFilters, AssessmentFilterGroupId>({
    tableName: 'assessments',
    defaultFilters,
    allFilterGroupIds: ALL_ASSESSMENT_FILTER_GROUP_IDS,
    layoutWidth: layoutWidth as ElementWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['assessments', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: () =>
      getAllAssessments(
        buildListParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
        }),
      ),
  });

  const data = apiResponse?.data || [];
  const totalItems = apiResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const {
    data: expandedAssessmentData,
    isLoading: isLoadingExpandedAssessment,
    isError: isExpandedAssessmentError,
    error: expandedAssessmentError,
  } = useQuery({
    queryKey: ['assessment', dt.expandedRowId],
    queryFn: async () => {
      if (!dt.expandedRowId) return null;
      const { getAssessment } = await import('@/services/assessments.service');
      return await getAssessment(dt.expandedRowId);
    },
    enabled: !!dt.expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedAssessmentError,
    error: expandedAssessmentError,
    catalogKey: 'assessmentNotFound',
    onClose: onCloseDetails,
    entityId: dt.expandedRowId,
  });

  const [deleteAssessmentId, setDeleteAssessmentId] = React.useState<string | null>(null);
  const [validateAssessmentId, setValidateAssessmentId] = React.useState<string | null>(null);

  /** Refresh list + single-assessment caches after mutate. */
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessments'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['assessment'], refetchType: 'all' }),
    ]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAssessment(id),
    onSuccess: async () => {
      await invalidate();
      setDeleteAssessmentId(null);
      toast.success('Bilan supprimé avec succès');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const validateMutation = useMutation({
    mutationFn: (id: string) => validateAssessment(id),
    onSuccess: async () => {
      await invalidate();
      setValidateAssessmentId(null);
      toast.success('Bilan validé avec succès');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  /** Download the validated assessment PDF. */
  const handleExport = React.useCallback(async (assessment: Assessment) => {
    try {
      await exportAssessment(assessment.id, assessment.type);
      toast.success('Export réussi');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  /** Any type / status / establishment / search value is set. */
  const hasActiveFilters = React.useMemo(
    () =>
      !!dt.filters.type ||
      !!dt.filters.status ||
      !!dt.filters.establishmentId ||
      !!dt.filters.search?.trim(),
    [dt.filters],
  );

  const filtersContent = React.useMemo(
    () => (
      <AssessmentFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onSetType={(type) => {
          dt.setFilters((prev) => ({ ...prev, type }));
          dt.setCurrentPage(1);
        }}
        onSetStatus={(status) => {
          dt.setFilters((prev) => ({ ...prev, status }));
          dt.setCurrentPage(1);
        }}
        onSetEstablishment={(establishmentId) => {
          dt.setFilters((prev) => ({ ...prev, establishmentId }));
          dt.setCurrentPage(1);
        }}
        onSetSearch={(search) => {
          dt.setFilters((prev) => ({ ...prev, search }));
          dt.setCurrentPage(1);
        }}
      />
    ),
    [dt],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const configureContent = React.useMemo(
    () => (
      <div className="flex flex-wrap gap-3">
        {ALL_ASSESSMENT_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map(
          (id) => {
            const isOn = dt.filterVisibility[id] ?? true;
            return (
              <Button
                key={id}
                variant="custom"
                size="sm"
                onClick={() => dt.handleToggleFilterVisibility(id)}
                disabled={dt.isSavingFilterVisibility}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                  dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                  isOn
                    ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                    : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                )}
              >
                {ASSESSMENT_FILTER_GROUP_LABELS[id]}
              </Button>
            );
          },
        )}
      </div>
    ),
    [dt],
  );

  const expandedAssessment = expandedAssessmentData ?? null;

  const assessmentToDelete = React.useMemo(
    () => (deleteAssessmentId ? data.find((b) => b.id === deleteAssessmentId) : null),
    [deleteAssessmentId, data],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="bilan(s)"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() =>
                dt.setShowFilters((prev) => {
                  if (!prev) dt.setShowConfigure(false);
                  return !prev;
                })
              }
            />
          )}
          {showCreateButton && onCreateAssessment && (
            <Button
              preset="toolbar-create"
              tooltipText="Générer un bilan"
              onClick={() => {
                onCloseDetails?.();
                onCreateAssessment();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="bilan(s)"
            onToggleConfigure={() => dt.setShowConfigure((prev) => !prev)}
            showConfigure={dt.showConfigure}
            configureContent={configureContent}
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Type</th>
          <th>Personne concernée</th>
          <th>Établissement</th>
          <th>Statut</th>
          <th>Généré le</th>
          <th className="w-48 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(assessment) => (
        <AssessmentRow
          assessment={assessment}
          onView={onToggleRow ? () => onToggleRow(assessment.id) : undefined}
          onEdit={onEditAssessment ? () => onEditAssessment(assessment.id) : undefined}
          onDelete={() => setDeleteAssessmentId(assessment.id)}
          onValidate={() => setValidateAssessmentId(assessment.id)}
          onExport={() => handleExport(assessment)}
          canValidate={canValidate}
          canExport={canExportCsv}
          pageActions={{ canEditRow, canDeleteRow, canViewRow: true }}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(assessment) => (
        <AssessmentDetailsPanel assessment={assessment} presentationMode="view" />
      )}
      renderCard={(assessment) => (
        <AssessmentCard
          assessment={assessment}
          onView={onToggleRow ? () => onToggleRow(assessment.id) : undefined}
          onEdit={onEditAssessment ? () => onEditAssessment(assessment.id) : undefined}
          onDelete={() => setDeleteAssessmentId(assessment.id)}
          onValidate={() => setValidateAssessmentId(assessment.id)}
          onExport={() => handleExport(assessment)}
          canValidate={canValidate}
          canExport={canExportCsv}
          pageActions={{ canEditRow, canDeleteRow, canViewRow: true }}
        />
      )}
      emptyIcon={ClipboardList}
      emptyText="Aucun bilan trouvé"
      title="Bilans"
      sidebarMenuId="menu-assessments"
      isSection={isSection}
      expandedItem={expandedAssessment}
      isExpandedDetailLoading={isLoadingExpandedAssessment}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(assessment) => (
        <AssessmentDetailsPanel assessment={assessment} presentationMode="view" />
      )}
      modals={
        <>
          <DeleteModal
            open={!!deleteAssessmentId}
            onClose={() => setDeleteAssessmentId(null)}
            onConfirm={() => {
              if (deleteAssessmentId) deleteMutation.mutate(deleteAssessmentId);
            }}
            itemName={
              assessmentToDelete
                ? `le bilan ${ASSESSMENT_TYPE_DEFINITIONS[assessmentToDelete.type].label.toLowerCase()} de ${formatAssessmentUserName(
                    assessmentToDelete.targetUser ?? assessmentToDelete.coordinatorUser,
                  )}`
                : 'ce bilan'
            }
            isLoading={deleteMutation.isPending}
          />
          <ValidateModal
            open={!!validateAssessmentId}
            onClose={() => setValidateAssessmentId(null)}
            onConfirm={() => {
              if (validateAssessmentId) void validateMutation.mutateAsync(validateAssessmentId);
            }}
            itemName="ce bilan"
            isLoading={validateMutation.isPending}
          />
        </>
      }
    />
  );
};
